import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { clearDashboardCache } from '../utils/dashboardCache';
import { exitPreview } from '../utils/preview';
import { leaveOpenClub } from '../utils/openClub';
import { setOfflineUser, forgetSavedPages } from '../utils/offline';
import { builtInAccessFor, canFor, STAFF_ROLES } from '../utils/access';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// The signed-in user, kept so the app still opens with no signal (sports halls).
// Only the token proves who someone is; this copy just lets the screens render.
const USER_KEY = 'tl-user';
const readCachedUser = () => {
  try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
};
const forgetCachedUser = () => {
  forgetSavedPages(readCachedUser()?.id);
  try { localStorage.removeItem(USER_KEY); } catch { /* storage unavailable */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  // Tell the offline store first: pages make their first requests in effects
  // that run before this provider's, and those must be saved for this user
  const setUser = (next) => {
    setOfflineUser(next?.id, next?.role);
    setUserState(next);
  };

  useEffect(() => {
    if (!user) return;
    try { localStorage.setItem(USER_KEY, JSON.stringify(user)); } catch { /* storage full or blocked */ }
  }, [user]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Configure axios defaults
  useEffect(() => {
    // Set base URL based on environment
    if (process.env.NODE_ENV === 'development') {
      // In development, use localhost backend
      axios.defaults.baseURL = 'http://localhost:5000';
    } else if (process.env.REACT_APP_API_URL) {
      // In production with explicit API URL
      axios.defaults.baseURL = process.env.REACT_APP_API_URL;
    }
    // In production without REACT_APP_API_URL, use relative URLs (same domain)
    
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  // Check if user is authenticated on mount (and again from the
  // "can't reach the server" screen)
  const [unavailable, setUnavailable] = useState(false);
  const checkAuth = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get('/api/auth/me');
        setUser(response.data);
        setUnavailable(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      const status = error.response?.status;
      const cached = readCachedUser();
      if ((status === 401 || status === 403) && exitPreview('/dashboard')) return;
      // An opened club's hour is up: back to the portal
      if ((status === 401 || status === 403) && leaveOpenClub()) return;
      if (!cached && (!error.response || status >= 500)) {
        // The server is unavailable (a 503 while its database is
        // unreachable, say) or there's no signal, and there are no saved
        // details to show: keep the token (it hasn't been refused) and
        // offer to try again, rather than signing out. A super admin
        // opening a club keeps that club this way.
        setUnavailable(true);
      } else if ((status === 401 || status === 403) || !cached) {
        // The server rejected the token (or there's nothing to fall back on)
        localStorage.removeItem('token');
        forgetCachedUser();
        clearDashboardCache();
        delete axios.defaults.headers.common['Authorization'];
      } else {
        // No signal or a server hiccup: stay signed in with the last-known
        // details and check again once the connection is back
        setUser(cached);
        window.addEventListener('online', () => {
          axios.get('/api/auth/me').then(r => setUser(r.data)).catch(err => {
            if ([401, 403].includes(err.response?.status)) logout();
          });
        }, { once: true });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sign-in answers don't say what each person can do (only /me does), so
  // staff fetch /me first and are signed in once, with their access, so the
  // app never opens on the wrong pages. If /me fails, the sign-in details
  // stand and can() falls back to the built-in grid for their role.
  const withAccess = async (signedIn) => {
    if (!STAFF_ROLES.includes(signedIn?.role) || signedIn?.access) {
      setUser(signedIn);
      return signedIn;
    }
    let next = signedIn;
    try {
      const response = await axios.get('/api/auth/me');
      if (response.data?.id === signedIn.id) next = response.data;
    } catch { /* keep the sign-in details */ }
    setUser(next);
    return next;
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/login', { email, password });
      const { user, token } = response.data;
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const signedIn = await withAccess(user);
      
      return { success: true, user: signedIn };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/register', userData);
      const { user, token } = response.data;
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const signedIn = await withAccess(user);
      
      return { success: true, user: signedIn };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // A club signing itself up at /start: the club and its admin in one step
  const registerClub = async (data) => {
    try {
      setError(null);
      const response = await axios.post('/api/club-signup', data);
      const { user, token } = response.data;
      if (!token || !user) return { success: false, error: 'Something went wrong. Please try again.' };
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const signedIn = await withAccess(user);
      return { success: true, user: signedIn };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Sign-up failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  function logout() {
    localStorage.removeItem('token');
    forgetCachedUser();
    clearDashboardCache();
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  }

  const devLogin = async (email = null) => {
    try {
      setError(null);
      console.log('DevLogin called with email:', email);
      const response = await axios.post('/api/auth/dev-login', { email });
      console.log('DevLogin response:', response.data);
      const { user, token } = response.data;
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const signedIn = await withAccess(user);
      
      return { success: true, user: signedIn };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Development login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const childLogin = async (firstName, lastName, accessCode) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/child-login', { 
        firstName, 
        lastName, 
        accessCode 
      });
      
      // Handle name disambiguation
      if (response.data.needsDisambiguation) {
        return { 
          success: false, 
          needsDisambiguation: true,
          gymnasts: response.data.gymnasts,
          error: response.data.message
        };
      }
      
      const { child, token } = response.data;
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Create a child user object that looks like a regular user
      const childUser = {
        ...child,
        role: 'CHILD',
        isChild: true
      };
      
      setUser(childUser);
      
      return { success: true, user: childUser };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Child login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const childLoginDisambiguate = async (gymnastId, accessCode) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/child-login-disambiguate', { 
        gymnastId, 
        accessCode 
      });
      const { child, token } = response.data;
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Create a child user object that looks like a regular user
      const childUser = {
        ...child,
        role: 'CHILD',
        isChild: true
      };
      
      setUser(childUser);
      
      return { success: true, user: childUser };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Child login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Generate code of the day
  const generateCodeOfTheDay = async (expiresInHours = 24) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/generate-code-of-day', { expiresInHours });
      return { 
        success: true, 
        codeOfTheDay: response.data.codeOfTheDay,
        expiresAt: response.data.expiresAt,
        isActive: true
      };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to generate code of the day';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Get current code of the day
  const getCodeOfTheDay = async () => {
    try {
      setError(null);
      const response = await axios.get('/api/auth/code-of-day');
      return { 
        success: true, 
        codeOfTheDay: response.data.codeOfTheDay,
        expiresAt: response.data.expiresAt,
        isActive: response.data.isActive
      };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to get code of the day';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Clear code of the day
  const clearCodeOfTheDay = async () => {
    try {
      setError(null);
      await axios.delete('/api/auth/code-of-day');
      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to clear code of the day';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Pages pass back the user from PUT /api/users/profile, which doesn't
  // include access or isClubAdmin: keep the ones we have
  const updateUser = (updatedUser) => {
    if (!updatedUser) { setUser(updatedUser); return; }
    setOfflineUser(updatedUser.id, updatedUser.role);
    setUserState(prev => {
      // Only carry them over for the same person
      if (prev?.id !== updatedUser.id) return updatedUser;
      const kept = {};
      if (updatedUser.access === undefined && prev?.access !== undefined) kept.access = prev.access;
      if (updatedUser.isClubAdmin === undefined && prev?.isClubAdmin !== undefined) kept.isClubAdmin = prev.isClubAdmin;
      return { ...updatedUser, ...kept };
    });
  };

  // What this person can do, area by area (utils/access.js). An older saved
  // copy of /me has no access, so use the built-in grid for their role.
  const access = user?.access || builtInAccessFor(user?.role, user?.actingAs);
  const can = canFor(access);

  // Role checks. isClubAdmin stays role-based: the roles page, RTC billing
  // and terms are for Club admins only.
  const isClubAdmin = user?.role === 'CLUB_ADMIN';
  const isAdult = user?.role === 'ADULT';
  const isChild = user?.role === 'CHILD';
  const isCoach = can('registers', 'MANAGE');
  const isWelfare = user?.role === 'WELFARE';
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isStaff = STAFF_ROLES.includes(user?.role);

  const value = {
    user,
    login,
    logout,
    register,
    error,
    loading,
    isClubAdmin,
    isAdult,
    isChild,
    isCoach,
    isWelfare,
    isSuperAdmin,
    isStaff,
    generateCodeOfTheDay,
    getCodeOfTheDay,
    clearCodeOfTheDay,
    childLogin,
    childLoginDisambiguate,
    devLogin,
    updateUser,
    registerClub,
    isAuthenticated: !!user,
    can,
    canManageClub: can('settings', 'MANAGE'),
    canManageGymnasts: can('members', 'MANAGE'),
    canMarkProgress: can('skillTracking', 'MANAGE'),
    canEditCompetitions: can('competitions', 'MANAGE'),
    canEditLevels: can('skillSetup', 'MANAGE'),
    canReadCompetitions: can('competitions'),
    canReadLevels: true, // All authenticated users can read levels
    canViewProgress: true, // All authenticated users can view progress (with backend access controls)
    canViewOwnProgress: user?.role === 'ADULT' || user?.role === 'CHILD', // Adults and children can view progress
    needsProgressNavigation: user?.role === 'CHILD', // Only children need progress navigation (tracking hidden from adults for now)
    needsShareCodeManagement: user?.role === 'ADULT', // Only adults need share code management
    needsCodeOfDayManagement: can('registers', 'MANAGE'), // the code of the day belongs to registers
    // Welfare access (never for a super admin who has opened the club)
    canViewWelfare: can('welfare'),
  };

  return (
    <AuthContext.Provider value={value}>
      {unavailable && !user ? (
        <div role="alert" style={{ maxWidth: 420, margin: '20vh auto 0', padding: '0 16px', textAlign: 'center' }}>
          <h2 style={{ marginBottom: '0.5rem' }}>We can't reach the server just now</h2>
          <p style={{ marginBottom: '1.25rem' }}>You're still signed in. Please try again in a moment.</p>
          <button type="button" onClick={checkAuth} disabled={loading} style={{ padding: '0.6rem 1.4rem', fontSize: '1rem', cursor: 'pointer' }}>
            {loading ? 'Trying...' : 'Try again'}
          </button>
          {/* A way out when the answer never changes (a fault for this one
              account): signing out leads to the sign-in page */}
          <p style={{ marginTop: '1rem' }}>
            <button type="button" onClick={() => { logout(); setUnavailable(false); }} style={{ background: 'none', border: 'none', padding: 0, textDecoration: 'underline', cursor: 'pointer', fontSize: '0.95rem' }}>
              Sign out
            </button>
          </p>
        </div>
      ) : children}
    </AuthContext.Provider>
  );
}; 